import VersusPage, {
  VersusRow,
  VersusFaq,
  VersusVendor,
} from '@comp/compare/versus-template'

type VersusContent = {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitleLines: [string, string]
  heroBody: string
  vendorA: VersusVendor
  vendorB: VersusVendor
  rows: VersusRow[]
  whenA: string[]
  whenB: string[]
  thirdOptionTitleLines: [string, string]
  thirdOptionBody: string
  thirdOptionPoints: string[]
  faqs: VersusFaq[]
}

const content: Record<'en' | 'tr', VersusContent> = {
  en: {
    seoTitle: 'Okta vs Microsoft Entra ID (2026), and the third option | Monofor',
    seoDescription:
      'An honest Okta vs Entra ID comparison: ecosystems, pricing, governance and PAM add-ons, and where a self-hosted unified platform like Monofor fits.',
    heroEyebrow: 'IdP comparison',
    heroTitleLines: ['Okta vs Entra ID.', 'Neutrality vs gravity.'],
    heroBody:
      'The most common identity bake-off in the enterprise: the neutral specialist against the ecosystem default. Here is how they actually differ, and the deployment question neither of them can answer.',
    vendorA: {
      name: 'Okta',
      summary:
        'The independent identity leader: a vendor-neutral cloud IdP with the largest third-party integration network and a product for nearly every workforce identity need.',
      strengths: [
        'Vendor-neutral: treats every app and cloud equally',
        '7,000+ app integration network with deep SAML/SCIM coverage',
        'Mature admin experience and documentation',
        'Broad product family across workforce and customer identity',
      ],
      considerations: [
        'SaaS-only: no self-hosted option for sovereignty requirements',
        'Compare suite limits and the total quote for your requirements',
        'Governance (OIG) and privileged access (OPA) are separate paid products',
        'Your identity plane depends on a third-party cloud’s availability',
      ],
    },
    vendorB: {
      name: 'Microsoft Entra ID',
      summary:
        'Microsoft’s identity platform (formerly Azure AD): the default choice for Microsoft-centric estates, bundled into M365 licensing with deep Windows and Azure integration.',
      strengths: [
        'Effectively included with M365 E3/E5 licensing many orgs already own',
        'Deep integration with Windows, Azure, and Office',
        'Strong conditional access engine',
        'Hybrid story with on-prem Active Directory',
      ],
      considerations: [
        'Optimized for the Microsoft ecosystem; third-party depth varies',
        'Full capabilities require P2 / E5 tiers and add-ons',
        'Complex licensing matrix across P1, P2, Governance, and Suite add-ons',
        'Non-Microsoft legacy apps often need extra tooling',
      ],
    },
    rows: [
      {
        dimension: 'Deployment model',
        a: 'SaaS only',
        b: 'SaaS (hybrid via AD Connect)',
        monofor: 'Self-hosted or SaaS, same product',
      },
      {
        dimension: 'Third-party app catalog',
        a: '7,000+ (OIN)',
        b: 'Large, Microsoft-first depth',
        monofor: '7,000+ pre-built',
      },
      {
        dimension: 'MFA incl. passkeys',
        a: 'Yes',
        b: 'Yes',
        monofor: 'Yes',
      },
      {
        dimension: 'Conditional / adaptive access',
        a: 'Yes',
        b: 'Yes, strong',
        monofor: 'Yes',
      },
      {
        dimension: 'Identity governance',
        detail: 'Access reviews, certification',
        a: 'Essentials+',
        sources: [{ label: 'Okta pricing', href: 'https://www.okta.com/pricing/' }],
        b: 'P2 / Governance add-on',
        monofor: 'Included',
      },
      {
        dimension: 'Privileged access management',
        a: 'Suite-dependent OPA',
        sources: [{ label: 'Okta pricing', href: 'https://www.okta.com/pricing/' }],
        b: 'PIM (Azure-centric)',
        monofor: 'Monopam; separate concurrent-session license',
      },
      {
        dimension: 'Legacy protocol bridges',
        detail: 'RADIUS, LDAP, header-based apps',
        a: 'RADIUS, LDAP Interface, Access Gateway',
        b: 'Partial (via Azure services)',
        monofor: 'RADIUS + LDAP + access gateway included',
      },
      {
        dimension: 'Vendor neutrality',
        a: 'High',
        b: 'Microsoft-centric',
        monofor: 'High',
      },
      {
        dimension: 'ITDR / identity risk',
        a: 'ITP; suite-dependent',
        b: 'Higher tier (P2)',
        monofor: 'Built in: SSF/CAEP both directions',
      },
      {
        dimension: 'AI agent identity governance',
        a: 'Okta for AI Agents',
        sources: [{ label: 'Okta for AI Agents', href: 'https://www.okta.com/products/govern-ai-agent-identity/' }, { label: 'Microsoft Entra Agent ID', href: 'https://learn.microsoft.com/en-us/entra/agent-id/what-is-microsoft-entra-agent-id' }],
        b: 'Entra Agent ID; security and governance require Agent 365',
        monofor: 'Built in, with an MCP gateway',
      },
      {
        dimension: 'Pricing',
        a: 'Starter: $6/user/month; annual',
        sources: [{ label: 'Okta pricing', href: 'https://www.okta.com/pricing/' }, { label: 'Monofor pricing', href: '/pricing' }],
        b: 'Bundled / P1 / P2 tiers + add-ons',
        monofor: 'Monosign from $4/user/mo; Monopam separately licensed',
      },
      {
        dimension: 'Data sovereignty control',
        a: 'Regional cloud tenants',
        b: 'Regional cloud tenants',
        monofor: 'Full: runs in your infrastructure',
      },
    ],
    whenA: [
      'Your stack is genuinely multi-vendor and you want an identity plane with no ecosystem agenda.',
      'Best-of-breed SaaS integration depth matters more than bundle economics.',
      'You are comfortable with cloud identity and suite-based licensing.',
    ],
    whenB: [
      'Your estate is Microsoft-first and E3/E5 licensing is already budgeted.',
      'Windows, Azure, and Office integration outweigh third-party depth.',
      'Hybrid AD continuity is a hard requirement.',
    ],
    thirdOptionTitleLines: [
      'Or take the neutrality',
      'without giving up your infrastructure.',
    ],
    thirdOptionBody:
      'Monosign is vendor-neutral like Okta, with a 7,000+ app catalog, and it deploys self-hosted in your own datacenter or cloud tenant, which neither Okta nor Entra ID offers. Monosign combines IAM and IGA from $4 per user per month. Monopam adds PAM with separate concurrent-session licensing.',
    thirdOptionPoints: [
      'Self-hosted or cloud: the sovereignty answer SaaS-only IdPs can’t give',
      'Monosign IAM + IGA; Monopam separately licensed',
      'Federates with Entra ID and consumes Okta during migration',
    ],
    faqs: [
      {
        q: 'Okta vs Entra ID: which should we choose?',
        a: 'If your estate is Microsoft-first and E3/E5 licensing is already on the table, Entra ID is hard to beat economically. If you run a heterogeneous stack and want an identity plane that treats every vendor equally, Okta’s neutrality and integration network are its case. The trade-offs are cost stacking on Okta’s side and ecosystem gravity on Microsoft’s.',
      },
      {
        q: 'Where does Monofor fit into this comparison?',
        a: 'Monofor keeps Okta-style vendor neutrality and a comparable app catalog, then adds what neither offers together: a self-hosted deployment option for sovereignty-bound organizations, plus Monosign governance and separately licensed Monopam privileged access in one platform.',
      },
      {
        q: 'Can Monofor coexist with Entra ID?',
        a: 'Yes, this is common. Entra ID stays as the Microsoft ecosystem directory while Monosign federates with it and handles the rest: non-Microsoft SaaS, legacy web apps via the access gateway, VPNs via RADIUS, and appliances via LDAP. You keep E3 value without forcing everything through one vendor.',
      },
      {
        q: 'What about migrating away from Okta?',
        a: 'Monosign consumes Okta as an upstream IdP during transition, so apps move one at a time with no sign-in disruption. Directory sync keeps both sides consistent until cutover, and a scoped product quote lets you compare total migration and operating costs.',
      },
    ],
  },
  tr: {
    seoTitle: 'Okta vs Microsoft Entra ID (2026) ve üçüncü seçenek | Monofor',
    seoDescription:
      'Dürüst bir Okta vs Entra ID karşılaştırması: ekosistemler, fiyatlandırma, yönetişim ve PAM eklentileri; ayrıca Monofor gibi kendi altyapınızda çalışan birleşik bir platformun konumu.',
    heroEyebrow: 'IdP karşılaştırması',
    heroTitleLines: ['Okta vs Entra ID.', 'Tarafsızlığa karşı çekim gücü.'],
    heroBody:
      'Kurumsal dünyanın en yaygın kimlik karşılaştırması: tarafsız uzman, ekosistemin varsayılanına karşı. İşte gerçekte nasıl ayrıştıkları ve ikisinin de yanıtlayamadığı kurulum sorusu.',
    vendorA: {
      name: 'Okta',
      summary:
        'Bağımsız kimlik lideri: en büyük üçüncü taraf entegrasyon ağına ve hemen her iş gücü kimlik ihtiyacına yönelik bir ürüne sahip, tedarikçi bağımsız bir bulut IdP’si.',
      strengths: [
        'Tedarikçi bağımsız: her uygulamaya ve buluta eşit davranır',
        'Derin SAML/SCIM kapsamına sahip 7.000+ uygulamalık entegrasyon ağı',
        'Olgun yönetim deneyimi ve dokümantasyon',
        'İş gücü ve müşteri kimliğini kapsayan geniş ürün ailesi',
      ],
      considerations: [
        'Yalnızca SaaS; egemenlik gereksinimleri için kendi altyapınızda kurulum seçeneği yok',
        'Gereksinimleriniz için paket sınırlarını ve toplam teklifi karşılaştırın',
        'Yönetişim (OIG) ve ayrıcalıklı erişim (OPA) ayrı ücretli ürünler',
        'Kimlik düzleminiz üçüncü taraf bir bulutun erişilebilirliğine bağlı',
      ],
    },
    vendorB: {
      name: 'Microsoft Entra ID',
      summary:
        'Microsoft’un kimlik platformu (eski adıyla Azure AD): Microsoft odaklı ortamlar için varsayılan tercih; M365 lisanslamasına dahil, Windows ve Azure ile derin entegrasyon sunar.',
      strengths: [
        'Birçok kuruluşun zaten sahip olduğu M365 E3/E5 lisanslarına fiilen dahil',
        'Windows, Azure ve Office ile derin entegrasyon',
        'Güçlü koşullu erişim motoru',
        'Şirket içi Active Directory ile hibrit senaryo',
      ],
      considerations: [
        'Microsoft ekosistemi için optimize; üçüncü taraf derinliği değişken',
        'Tam yetenekler P2 / E5 katmanları ve eklentiler gerektirir',
        'P1, P2, Governance ve Suite eklentileri arasında karmaşık lisanslama matrisi',
        'Microsoft dışı eski uygulamalar çoğu zaman ek araç gerektirir',
      ],
    },
    rows: [
      {
        dimension: 'Kurulum modeli',
        a: 'Yalnızca SaaS',
        b: 'SaaS (AD Connect ile hibrit)',
        monofor: 'Kendi altyapınızda veya SaaS, aynı ürün',
      },
      {
        dimension: 'Üçüncü taraf uygulama kataloğu',
        a: '7.000+ (OIN)',
        b: 'Geniş, Microsoft öncelikli derinlik',
        monofor: '7.000+ hazır entegrasyon',
      },
      {
        dimension: 'MFA (passkey dahil)',
        a: 'Evet',
        b: 'Evet',
        monofor: 'Evet',
      },
      {
        dimension: 'Koşullu / uyarlanabilir erişim',
        a: 'Evet',
        b: 'Evet, güçlü',
        monofor: 'Evet',
      },
      {
        dimension: 'Kimlik yönetişimi',
        detail: 'Erişim incelemeleri, sertifikasyon',
        a: 'Essentials+',
        sources: [{ label: 'Okta fiyatlandırma', href: 'https://www.okta.com/pricing/' }],
        b: 'P2 / Governance eklentisi',
        monofor: 'Dahil',
      },
      {
        dimension: 'Ayrıcalıklı erişim yönetimi',
        a: 'Pakete bağlı OPA',
        sources: [{ label: 'Okta fiyatlandırma', href: 'https://www.okta.com/pricing/' }],
        b: 'PIM (Azure odaklı)',
        monofor: 'Monopam; ayrı eşzamanlı oturum lisansı',
      },
      {
        dimension: 'Eski protokol köprüleri',
        detail: 'RADIUS, LDAP, header tabanlı uygulamalar',
        a: 'RADIUS, LDAP Interface, Access Gateway',
        b: 'Kısmi (Azure servisleri ile)',
        monofor: 'RADIUS + LDAP + erişim ağ geçidi dahil',
      },
      {
        dimension: 'Tedarikçi bağımsızlığı',
        a: 'Yüksek',
        b: 'Microsoft odaklı',
        monofor: 'Yüksek',
      },
      {
        dimension: 'ITDR / kimlik riski',
        a: 'ITP; pakete bağlı',
        b: 'Üst katman (P2)',
        monofor: 'Yerleşik: SSF/CAEP çift yön',
      },
      {
        dimension: 'AI ajan kimliği yönetişimi',
        a: 'Okta for AI Agents',
        sources: [{ label: 'Okta for AI Agents', href: 'https://www.okta.com/products/govern-ai-agent-identity/' }, { label: 'Microsoft Entra Agent ID', href: 'https://learn.microsoft.com/en-us/entra/agent-id/what-is-microsoft-entra-agent-id' }],
        b: 'Entra Agent ID; güvenlik ve yönetişim için Agent 365 gerekir',
        monofor: 'Yerleşik, MCP geçidiyle',
      },
      {
        dimension: 'Fiyatlandırma',
        a: 'Starter: 6 $/kullanıcı/ay; yıllık',
        sources: [{ label: 'Okta fiyatlandırma', href: 'https://www.okta.com/pricing/' }, { label: 'Monofor fiyatlandırma', href: '/tr/pricing' }],
        b: 'Paket / P1 / P2 katmanları + eklentiler',
        monofor: 'Monosign 4 $/kullanıcı/ay’dan; Monopam ayrı lisanslı',
      },
      {
        dimension: 'Veri egemenliği kontrolü',
        a: 'Bölgesel bulut tenant’ları',
        b: 'Bölgesel bulut tenant’ları',
        monofor: 'Tam: kendi altyapınızda çalışır',
      },
    ],
    whenA: [
      'Yığınınız gerçekten çok tedarikçili ve ekosistem gündemi olmayan bir kimlik düzlemi istiyorsunuz.',
      'En iyi SaaS entegrasyon derinliği, paket ekonomisinden daha önemli.',
      'Bulut kimlik yönetimi ve paket tabanlı lisanslama sizin için uygundur.',
    ],
    whenB: [
      'Ortamınız Microsoft öncelikli ve E3/E5 lisansları zaten bütçelenmiş.',
      'Windows, Azure ve Office entegrasyonu, üçüncü taraf derinliğinden daha ağır basıyor.',
      'Hibrit AD sürekliliği kesin bir gereksinim.',
    ],
    thirdOptionTitleLines: [
      'Ya da altyapınızdan vazgeçmeden',
      'tarafsızlığı seçin.',
    ],
    thirdOptionBody:
      'Monosign, Okta gibi tedarikçi bağımsızdır ve 7.000+ uygulamalık bir kataloğa sahiptir; üstelik ne Okta’nın ne de Entra ID’nin sunabildiği şekilde, kendi veri merkezinizde veya bulut tenant’ınızda kurulabilir. Monosign, IAM ve IGA’yı kullanıcı başına aylık 4 $’dan sunar. Monopam, ayrı eşzamanlı oturum lisansıyla PAM sağlar.',
    thirdOptionPoints: [
      'Kendi altyapınızda veya bulutta: yalnızca SaaS IdP’lerin veremediği egemenlik yanıtı',
      'Monosign IAM + IGA; Monopam ayrıca lisanslanır',
      'Entra ID ile federasyon kurar, geçiş sırasında Okta’yı kullanır',
    ],
    faqs: [
      {
        q: 'Okta vs Entra ID: hangisini seçmeliyiz?',
        a: 'Ortamınız Microsoft öncelikliyse ve E3/E5 lisanslaması zaten masadaysa, Entra ID’yi ekonomik olarak geçmek zordur. Heterojen bir yığın çalıştırıyorsanız ve her tedarikçiye eşit davranan bir kimlik düzlemi istiyorsanız, Okta’nın tarafsızlığı ve entegrasyon ağı en güçlü argümanıdır. Ödünler ise Okta tarafında maliyet birikimi, Microsoft tarafında ekosistem çekimidir.',
      },
      {
        q: 'Monofor bu karşılaştırmanın neresinde?',
        a: 'Monofor, Okta tarzı tedarikçi bağımsızlığını ve benzer bir uygulama kataloğunu korur; ardından ikisinin birlikte sunmadığını ekler: egemenlik gereksinimi olan kuruluşlar için kendi altyapınızda kurulum seçeneği, artı aynı platformda Monosign yönetişimi ve ayrıca lisanslanan Monopam ayrıcalıklı erişimi.',
      },
      {
        q: 'Monofor, Entra ID ile birlikte çalışabilir mi?',
        a: 'Evet, bu yaygın bir senaryo. Entra ID, Microsoft ekosisteminin dizini olarak kalır; Monosign onunla federasyon kurar ve geri kalanı üstlenir: Microsoft dışı SaaS, erişim ağ geçidi üzerinden eski web uygulamaları, RADIUS ile VPN’ler ve LDAP ile cihazlar. Her şeyi tek tedarikçiden geçirmeden E3 değerini korursunuz.',
      },
      {
        q: 'Peki Okta’dan geçiş?',
        a: 'Monosign, geçiş sırasında Okta’yı üst IdP olarak kullanır; böylece uygulamalar oturum açma kesintisi olmadan tek tek taşınır. Dizin eşitlemesi, geçiş tamamlanana kadar iki tarafı tutarlı tutar ve ürün kapsamına uygun bir teklif, toplam geçiş ve işletim maliyetlerini karşılaştırmanıza yardımcı olur.',
      },
    ],
  },
}

type Props = {
  locale: string
}

export default function OktaVsEntraIdPage({ locale }: Props) {
  const c = content[locale === 'tr' ? 'tr' : 'en']
  return (
    <VersusPage
      locale={locale}
      bilingual
      path="/compare/okta-vs-entra-id"
      seoTitle={c.seoTitle}
      seoDescription={c.seoDescription}
      heroEyebrow={c.heroEyebrow}
      heroTitle={
        <>
          {c.heroTitleLines[0]}
          <br />
          {c.heroTitleLines[1]}
        </>
      }
      heroBody={c.heroBody}
      vendorA={c.vendorA}
      vendorB={c.vendorB}
      comparisonNote={locale === 'tr'
        ? 'Genel karşılaştırma Temmuz 2026 bilgilerine dayanır. Okta paketleri ve iki tedarikçinin AI ajan belgeleri 22 Eylül 2026 tarihinde gözden geçirildi.'
        : 'General comparison reflects July 2026 information. Okta packaging and both vendors’ AI agent documentation were reviewed on September 22, 2026.'}
      rows={c.rows}
      whenA={c.whenA}
      whenB={c.whenB}
      thirdOptionTitle={
        <>
          {c.thirdOptionTitleLines[0]}
          <br />
          {c.thirdOptionTitleLines[1]}
        </>
      }
      thirdOptionBody={c.thirdOptionBody}
      thirdOptionPoints={c.thirdOptionPoints}
      faqs={c.faqs}
    />
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
